import { Component, EventEmitter, Input, OnDestroy, Output, ViewEncapsulation } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { mdTransitionAnimation } from '@ionic/angular';

import { Subject, takeUntil, timer } from 'rxjs';

import {
  LoyaltyProgramModalPage,
  LoyaltyProgramModalPageIdentifier,
} from '../../../modals/loyalty-program-modal/loyalty-program-modal.page';

import { LoyaltyProgramStepStatus } from '../../../core/enums/loyalty-program-step-status.enum';

import { LoyaltyProgramDetails } from '../../../core/models/loyalty-program-details.model';

import { DateTimeService } from '../../../core/services/date-time.service';
import { ModalService } from '../../../core/services/modal.service';
import { NavigationService } from '../../../core/services/navigation.service';
import { PlatformService } from '../../../core/services/ssr/platform.service';

import { TermsAndConditionsPageParams } from '../../../pages/terms-and-conditions/terms-and-conditions-routing.module';

import { Helpers } from '../../../core/helpers';

@Component({
  selector: 'app-loyalty-program-status',
  templateUrl: './loyalty-program-status.component.html',
  styleUrls: ['./loyalty-program-status.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LoyaltyProgramStatusComponent implements OnDestroy {
  @Input()
  public isReloadingDetails: boolean;

  @Output()
  public reloadRequested = new EventEmitter<void>();

  public currentStepIndex: number;
  public deadlineDateTimeText: string;
  public loyaltyProgramDetails: LoyaltyProgramDetails;
  public LoyaltyProgramStepStatus = LoyaltyProgramStepStatus;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private modalService: ModalService,
    private dateTimeService: DateTimeService,
    private platformService: PlatformService,
    private translateService: TranslateService,
    private navigationService: NavigationService,
  ) {}

  @Input()
  public set details(details: LoyaltyProgramDetails) {
    const nextUnknownStep = details.steps.findIndex((step) => step.status === LoyaltyProgramStepStatus.Unknown);
    this.loyaltyProgramDetails = details;
    this.currentStepIndex = Math.max(0, (nextUnknownStep === -1 ? this.loyaltyProgramDetails.steps.length : nextUnknownStep) - 1);

    // Interval and timer operators doesn't work in SSR
    // https://stackoverflow.com/a/57456495/3915438
    if (!this.platformService.isServer) {
      timer(0, 1000)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => {
          const timeUntilDeadline = this.dateTimeService.getTimeUntilDeadline(this.loyaltyProgramDetails.endDate);
          const days = `${timeUntilDeadline.days} ${(
            this.translateService.instant(timeUntilDeadline.days === 1 ? 'DAY' : 'DAYS') as string
          ).toLowerCase()}`;
          const hours = Helpers.padNumber(timeUntilDeadline.hours);
          const minutes = Helpers.padNumber(timeUntilDeadline.minutes);
          const seconds = Helpers.padNumber(timeUntilDeadline.seconds);

          this.deadlineDateTimeText = `${days} ${hours}:${minutes}:${seconds}`;
        });
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public onOpenTermsAndConditionsModal(): void {
    if (this.isReloadingDetails) {
      return;
    }

    void this.modalService.showAlert<void>({
      component: LoyaltyProgramModalPage,
      id: LoyaltyProgramModalPageIdentifier,
      componentProps: {
        title: this.translateService.instant('LOYALTY_PROGRAM_STATUS_COMPONENT.TERMS_AND_CONDITIONS') as string,
        description: this.loyaltyProgramDetails.termsAndConditions,
        descriptionAdditionalContent: [
          {
            text: this.translateService.instant('LOYALTY_PROGRAM_STATUS_COMPONENT.TERMS_AND_CONDITIONS_ADDITIONAL_DETAILS') as string,
            handler: () => {
              const pageParams: TermsAndConditionsPageParams = {
                termsAndConditions: this.translateService.instant('PAGE_CONTENT.TERMS_AND_CONDITIONS') as string,
              };

              this.platformService.isBrowser
                ? void this.navigationService.navigateTo('/terms')
                : void this.navigationService.navigateTo('/terms-conditions', false, {
                    state: pageParams,
                    animation: mdTransitionAnimation,
                  });
            },
          },
        ],
        descriptionCentered: false,
        addLinkToGeneralTermsAndConditions: true,
      },
    });
  }

  public onRequestReload(): void {
    if (this.isReloadingDetails) {
      return;
    }

    this.reloadRequested.emit();
  }

  public onOpenRewardDetails(stepIndex: number): void {
    const selectedStep = this.loyaltyProgramDetails.steps[stepIndex];

    if (!selectedStep || !selectedStep.rewardDetailsText) {
      return;
    }

    void this.modalService.showAlert<void>({
      component: LoyaltyProgramModalPage,
      id: LoyaltyProgramModalPageIdentifier,
      componentProps: {
        headerImageUrl: selectedStep.imageUrl,
        description: selectedStep.rewardDetailsText,
      },
    });
  }
}
